import React, { useEffect, useMemo } from 'react';

import { addMinutes, isBefore, subMinutes } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';
import { useStoreContext } from 'features/store';
import { DEFAULT_STORE_TIMEZONE } from 'features/store/constants';
import {
  getSelectedStoreCurrentTimeUTC,
  getStoreCurrentTimeUTC,
  getStoreOperatingHoursForServiceMode,
  isStoreOpen,
} from 'features/store/utils';
import { FlatList } from 'react-native';

import { useCart } from 'hooks/use-cart';
import { getIsMobileWeb } from 'hooks/use-media-query';
import { formatTime, t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useServiceModeLive } from 'state/service-mode';
import { spacing } from 'styles/constants';
import { isNative } from 'utils/environment';

import { PressableTime, TimeText } from './styled';

const OPTIONS_LENGTH = 5;
const INTERVAL_MINS = 15;
const DAY_PART_END_AHEAD_MINUTES = 10;

const PickupTimeSelector = ({ isDisabled = false }: { isDisabled?: boolean }) => {
  const { store } = useStoreContext();
  const { pickupDate, setPickupDate } = useOrderContext();
  const serviceMode = useServiceModeLive();
  const { cartEntries } = useCart();
  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);
  const isMobileWeb = getIsMobileWeb();
  const storeTimeZone = store.timezone || DEFAULT_STORE_TIMEZONE;

  // Only desktop any size should show the scroll indicator
  const showsHorizontalScrollIndicator = !isMobileWeb || !isNative;

  // always reset to now when you mount cart (only happens if you leave the whole flow)
  useEffect(() => {
    setPickupDate(null);
  }, []);

  const timeOptions = useMemo<Date[]>(() => {
    const ORDER_NOW = (null as unknown) as Date;

    if (!serviceMode) {
      return [ORDER_NOW];
    }

    const hours = getStoreOperatingHoursForServiceMode(store, serviceMode);

    if (!hours) {
      return [ORDER_NOW];
    }

    const storeCurrentDate = getStoreCurrentTimeUTC(store);
    const currentMinutes = storeCurrentDate.getUTCMinutes();
    const mod5Remainder = currentMinutes % 5;
    const epochRoundedTo5 = new Date(storeCurrentDate);
    epochRoundedTo5.setUTCMinutes(currentMinutes - mod5Remainder);
    epochRoundedTo5.setUTCSeconds(0);
    epochRoundedTo5.setUTCMilliseconds(0);

    const areItemsAvailableForPickupAt = (pickupTimeUTC: Date) => {
      const noCartEntryDayPartEndsBeforePickup = !cartEntriesActiveDayParts.some(
        cartEntryActiveDayPart => {
          const endTimeUTC = zonedTimeToUtc(cartEntryActiveDayPart.endTime, storeTimeZone);
          const lastCall = subMinutes(endTimeUTC, DAY_PART_END_AHEAD_MINUTES);
          return isBefore(lastCall, pickupTimeUTC);
        }
      );

      return cartEntriesActiveDayParts.length > 0 && noCartEntryDayPartEndsBeforePickup;
    };

    const filteredOptions = Array.from({ length: OPTIONS_LENGTH })
      .map((_x, idx) => addMinutes(epochRoundedTo5, (idx + 1) * INTERVAL_MINS))
      .filter(
        pickupTime =>
          isStoreOpen({
            currentStoreDate: pickupTime,
            store,
            serviceModes: [serviceMode],
          }) && areItemsAvailableForPickupAt(pickupTime)
      );

    return [ORDER_NOW].concat(filteredOptions);
  }, [cartEntriesActiveDayParts, serviceMode, store, storeTimeZone]);

  return (
    <FlatList
      style={{
        paddingVertical: spacing.$3,
      }}
      testID="pickup-time-selector"
      data={timeOptions}
      keyExtractor={(item: Date) => item?.getTime()?.toString() || '0'}
      renderItem={({ item: date }: { item: Date }) => {
        const isSelected =
          (date === null && pickupDate === null) || date?.getTime() === pickupDate?.getTime();
        return (
          <PressableTime
            onPress={() => {
              const currentTime = getSelectedStoreCurrentTimeUTC();
              const timedFireMinutes = date
                ? Math.round((date?.getTime() - currentTime.getTime()) / 1000 / 60)
                : 0;
              logEvent(CustomEventNames.SELECT_TIMED_FIRE_OPTION, EventTypes.Other, {
                'Timed Fire Minutes': timedFireMinutes,
              });
              setPickupDate(date);
            }}
            $isSelected={isSelected}
            disabled={isDisabled}
          >
            <TimeText $isSelected={isSelected}>
              {date === null
                ? t('now')
                : formatTime(date, {
                    hour: 'numeric',
                    minute: 'numeric',
                    timeZone: storeTimeZone,
                  })}
            </TimeText>
          </PressableTime>
        );
      }}
      horizontal
      showsHorizontalScrollIndicator={showsHorizontalScrollIndicator}
    />
  );
};

export default PickupTimeSelector;
